/**
 * ResolveEventDialog — modal de résolution d'un intake_event vers un projet
 * (Chunk 9.G.2 frontend, parité pattern RevokeIntakeLinkDialog).
 *
 * Phase 1 décisions PO lockées :
 *   - Q1=B project-only : pas de QuoteRequestPicker. Le payload pousse
 *     `{resolution_kind: "project", target_id: <project uuid>}` strict.
 *     Quote_request resolve UI = dette P3 Phase 2.
 *   - Q2=A 2 dialogs séparés (ResolveDialog + RejectDialog).
 *   - Q5=B race 409 INVALID_TRANSITION : auto-close + parent refetch via
 *     queryClient.invalidateQueries (déclenché par useResolveIntakeEvent.onSuccess
 *     OU manuellement en cas d'erreur 409 via le hook ?). Phase 1 simple :
 *     onError 409 → close dialog + toast info + invalidate via hook onSuccess
 *     ne fire pas sur erreur, donc on invalidate manuellement.
 *   - Q7=A pas de double confirm. Submit disabled tant que target_id vide.
 *
 * Pattern dialog parité [RevokeIntakeLinkDialog] :
 *   - `role="dialog"` + `aria-modal` + `aria-labelledby`
 *   - Focus initial sur Cancel (anti-clic-réflexe destructive — moins pertinent
 *     ici car action constructive, mais cohérence pattern)
 *   - Esc + backdrop click → close (si pas en cours de submit)
 *   - Body scroll lock pendant ouverture (héritage Tailwind)
 *
 * Erreurs :
 *   - 404 INTAKE_EVENTS_NOT_FOUND → bandeau red inline
 *   - 404 INTAKE_EVENTS_RESOLVE_TARGET_NOT_FOUND → bandeau red inline (target déplacé/supprimé)
 *   - 409 INTAKE_EVENTS_INVALID_TRANSITION → message info bilingue + close dialog + invalidate
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProjectPicker } from "../../../components/ProjectPicker";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useResolveIntakeEvent } from "../../../lib/queries/intakeLinks";
import { toast } from "../../../lib/toast";

type Props = {
  readonly open: boolean;
  readonly receiptKind?: "public_submission" | "voluntary_email";
  readonly eventId: string | null;
  /**
   * UUID intake_link parent — `null` pour les intake_events orphans (9.T.3
   * broker upload : `intake_link_id=NULL`). Quand null, la cache invalidation
   * du link détail est sautée (rien à invalider) et le 409 race lost
   * invalide à la place la query event détail.
   */
  readonly linkId: string | null;
  readonly onClose: () => void;
  readonly onResolved: () => void;
};

export function ResolveEventDialog({
  open,
  eventId,
  linkId,
  receiptKind = "public_submission",
  onClose,
  onResolved,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const copyKey =
    receiptKind === "voluntary_email"
      ? "voluntary_email.resolve"
      : "intake_links.detail.events.actions.resolve";
  const titleId = useId();
  const descId = useId();
  const projectFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();

  const resolve = useResolveIntakeEvent();
  const { reset: resetMutation } = resolve;

  // État local du formulaire — project_id sélectionné via ProjectPicker.
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  // Reset à chaque ouverture + focus initial Cancel.
  useEffect(() => {
    if (open) {
      resetMutation();
      setProjectId(undefined);
      queueMicrotask(() => cancelBtnRef.current?.focus());
    }
  }, [open, resetMutation]);

  // Esc → close (sauf pendant submit).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !resolve.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, resolve.isPending]);

  if (!open || !eventId) return null;

  const onConfirm = () => {
    if (!projectId) return;
    resolve.mutate(
      {
        eventId,
        linkId,
        input: { resolution_kind: "project", target_id: projectId },
      },
      {
        onSuccess: () => onResolved(),
        onError: (err) => {
          // 409 race condition (Q5=B) : auto-refetch + close + toast info.
          // L'autre broker a déjà résolu/rejeté — l'event est désormais terminal,
          // pas de sens de garder le dialog ouvert.
          if (err instanceof ApiException && err.status === 409) {
            void queryClient.invalidateQueries({ queryKey: ["intake-events", "by-link"] });
            void queryClient.invalidateQueries({ queryKey: ["intake-events", "list"] });
            if (linkId) {
              void queryClient.invalidateQueries({
                queryKey: ["intake-links", "detail", linkId],
              });
            }
            void queryClient.invalidateQueries({
              queryKey: ["intake-events", "detail", eventId],
            });
            toast.info(t("intake_links.detail.events.actions.race_lost"));
            onClose();
          }
          // 404/422/500 reste inline (le bandeau s'affiche via resolve.error).
        },
      },
    );
  };

  const apiErrorMsg =
    resolve.error instanceof ApiException && resolve.error.status !== 409
      ? getApiErrorMessage(resolve.error, lang) || t("errors.generic")
      : resolve.isError && !(resolve.error instanceof ApiException && resolve.error.status === 409)
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!resolve.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t(`${copyKey}.title`)}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t(`${copyKey}.body`)}
          </p>

          <div>
            <label
              htmlFor={projectFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t(`${copyKey}.project_label`)}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </label>
            <ProjectPicker
              id={projectFieldId}
              value={projectId}
              onChange={(id) => setProjectId(id)}
              disabled={resolve.isPending}
            />
            <p className="mt-1.5 text-xs text-slate-500">{t(`${copyKey}.project_hint`)}</p>
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={resolve.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={resolve.isPending || !projectId}
            aria-busy={resolve.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {resolve.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {resolve.isPending ? t(`${copyKey}.submitting`) : t(`${copyKey}.submit`)}
          </button>
        </footer>
      </div>
    </div>
  );
}
